import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useResponsiveStore,
  // useViewportStates,
  ResponsiveBreakpoint,
} from "@/stores/editor/responsive-store";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useVisualFeedback } from "./VisualFeedback/VisualFeedbackProvider";
import {
  useRealtimeCSS,
  useResponsiveCSSTrigger,
} from "@/hooks/useRealtimeCSS";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useCollaborationLegacy } from "./CollaborationProvider";
import { Component, Position, ComponentType, Size, Viewport, CanvasSettings } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import { SketchOverlay } from "./SketchOverlay";
import { InteractionPreview } from "./InteractionPreview";
import { calculateCanvasContentBounds } from "@/utils/canvas-dimensions";
import {
  calculateResponsivePosition,
  calculateResponsiveSize,
  applyResponsiveConstraints,
} from "@/utils/responsive-position-calculator";
import { MemoizedComponentTooltip } from "./ComponentTooltip";
import { useSelectionKeyboardShortcuts } from "@/hooks/editor/useSelectionKeyboardShortcuts";
import {
  type StyleBreakpoint,
  withNormalizedComponentStyle,
} from "@/utils/component-style-normalization";
import { normalizeLegacyCanvasBackgroundColor } from "@/utils/theme-aware-legacy-colors";

interface DefaultComponentData {
  defaultProperties?: Record<string, unknown>;
  defaultSize?: Size;
  defaultStyle?: Record<string, unknown>;
}

interface SpatialPerformanceBenchmark {
  avgQueryTime: number;
  queriesPerSecond: number;
  performanceRating: string;
}

interface LargeScalePerformanceResult {
  componentCount: number;
  insertDuration: string;
  avgQueryTime: string;
  avgCollisionTime: string;
  cullingDuration: string;
  cullingRatio: string;
  totalDuration: string;
  performanceRating: string;
  targetAchieved: boolean;
  memorySizeEstimate: string;
}

declare global {
  interface Window {
    benchmarkSpatialPerformance?: () => Promise<SpatialPerformanceBenchmark | undefined>;
    testLargeScalePerformance?: () => Promise<LargeScalePerformanceResult[] | undefined>;
  }
}

const cullingLogTimestamps = new Map<string, number>();
const cullingWarnTimestamps = new Map<string, number>();

const toFiniteNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const getEditorCanvasBackgroundStyle = (
  background: CanvasSettings["background"],
): React.CSSProperties => {
  if (!background || typeof background !== "object") return {};

  if (background.type === "color" && typeof background.color === "string") {
    return {
      backgroundColor: normalizeLegacyCanvasBackgroundColor(background.color),
    };
  }

  if (
    background.type === "gradient" &&
    background.gradient
  ) {
    if (Array.isArray(background.gradient.stops)) {
      return {
        background: `linear-gradient(${
          background.gradient.direction || "0deg"
        }, ${background.gradient.stops
          .map((stop) => `${normalizeLegacyCanvasBackgroundColor(stop.color)} ${stop.position}%`)
          .join(", ")})`,
      };
    }
  }

  return {};
};

// Helper function to get default properties for component types
const getDefaultPropertiesForType = (
  componentType: ComponentType,
): Record<string, unknown> => {
  switch (componentType) {
    case "text":
      return {
        content: "Sample Text",
        fontSize: 16,
        fontWeight: "normal",
        textAlign: "left",
        fontFamily: "Arial, sans-serif",
      };
    case "button":
      return {
        text: "Button",
        variant: "primary",
        disabled: false,
      };
    case "image":
      return {
        src: "",
        alt: "Image",
        objectFit: "cover",
      };
    case "container":
      return {
        padding: 16,
        backgroundColor: "transparent",
        borderRadius: 0,
      };
    case "flex-container":
      return {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        gap: 16,
        padding: 16,
        backgroundColor: "transparent",
        borderRadius: 8,
        minHeight: 100,
        label: "Flex Container",
      };
    default:
      return {};
  }
};
import {
  Rectangle,
  PerformanceMetrics,
  ComponentBounds,
  PERFORMANCE_CONFIG,
} from "@/types/canvas-legacy-types";
import { SharedComponentRenderer as ComponentRenderer } from "@/components/shared/rendering/SharedComponentRenderer";
// [Resize Removal] Importing SelectableComponent (renamed from ResizableComponent)
// Manual resize handles removed as part of AI-first architecture
import { SelectableComponent } from "./SelectableComponent";
import { ResponsiveEditorCanvas } from "./ResponsiveEditorCanvas";
import { Card } from "@/components/ui/card";
import { Eye } from "lucide-react";
import { useOptionalTheme } from "@/design-system/themes/theme-context";
import { logger } from "@/utils/logger";

interface MultiViewportCanvasProps {
  className?: string;
}

/**
 * Spatial index interface for type safety (stub - spatial indexing has been removed)
 * NOTE: This interface is kept for backward compatibility but spatial indexing is disabled.
 */
interface SpatialIndex {
  updateComponent: (id: string, bounds: ComponentBounds) => void;
  insert: (bounds: ComponentBounds) => void;
  remove: (id: string) => void;
  query: (bounds: Rectangle) => ComponentBounds[];
  queryRange: (options: { bounds: Rectangle; maxResults?: number; sorted?: boolean }) => ComponentBounds[];
  cullByViewport: (bounds: Rectangle, buffer?: number) => { visible: ComponentBounds[]; culled: ComponentBounds[] };
  size: () => number;
  getBounds: (id: string) => ComponentBounds | null;
  getMetrics: () => PerformanceMetrics;
  getAllComponents: () => ComponentBounds[];
  findCollisions: (bounds: ComponentBounds) => ComponentBounds[];
  clear: () => void;
}

interface SpatialIndexingState {
  spatialIndex: SpatialIndex | null; // Spatial indexing removed but type kept for compatibility
  registeredComponents: Map<string, ComponentBounds>;
  performanceMetrics: PerformanceMetrics;
  isInitialized: boolean;
}

interface ViewportCanvasProps {
  viewport: ResponsiveBreakpoint;
  isActive: boolean;
  isVisible: boolean;
  scale: number;
  components: Component[];
  selectedComponents: string[];
  hoveredComponent?: string;
  onComponentSelect: (id: string, ctrlKey?: boolean) => void;
  onCanvasClick: (e: React.MouseEvent) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  onRegisterRef?: (element: HTMLDivElement | null) => void;
  onComponentAdd?: (componentType: ComponentType, position: Position) => void;
  onComponentMove?: (componentId: string, position: Position) => void;
  className?: string;
  spatialIndexingState?: SpatialIndexingState;
}

interface ResponsiveComponentProps {
  component: Component;
  viewport: ResponsiveBreakpoint;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: (id: string, ctrlKey?: boolean) => void;
  scale: number;
  activeColorScheme?: ColorScheme;
}

const ResponsiveComponent: React.FC<
  ResponsiveComponentProps
> = ({ component, viewport, isSelected, isHovered, onSelect, scale, activeColorScheme }) => {
  const { getResponsiveProperty } = useResponsiveStore();
  const [_localMousePosition, setLocalMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const collaboration = useCollaborationLegacy();
  const { setHoveredComponent, hoveredComponent } = useEditorStore();
  const activeBreakpoint: StyleBreakpoint =
    viewport.id === "mobile" || viewport.id === "tablet" || viewport.id === "desktop"
      ? viewport.id
      : "desktop";
  const renderComponent = useMemo(
    () => withNormalizedComponentStyle(component, activeBreakpoint, activeColorScheme),
    [component, activeBreakpoint, activeColorScheme],
  );

  // Get responsive properties for this viewport with intelligent fallback
  const responsivePropertyX = getResponsiveProperty(component.id, "position.x", viewport.id);
  const responsivePropertyY = getResponsiveProperty(component.id, "position.y", viewport.id);
  const responsivePropertyZ = getResponsiveProperty(component.id, "position.z", viewport.id);

  const isNested = !!component.parentId;
  const basePosition = renderComponent.position || { x: 0, y: 0, z: 1 };

  const responsiveX = isNested
    ? responsivePropertyX ?? basePosition.x
    : basePosition.x;
  const responsiveY = isNested
    ? responsivePropertyY ?? basePosition.y
    : basePosition.y;
  const responsiveZ = isNested
    ? responsivePropertyZ ?? basePosition.z
    : basePosition.z;

  // Debug logging moved to prevent infinite render loop

  // Debug logging removed to prevent infinite render loop
  const responsiveWidth =
    getResponsiveProperty(component.id, "size.width", viewport.id) ??
    renderComponent.size?.width ??
    100;
  const responsiveHeight =
    getResponsiveProperty(component.id, "size.height", viewport.id) ??
    renderComponent.size?.height ??
    50;

  // Apply viewport-specific constraints and optimizations
  const constrainedPosition = useMemo(() => {
    if (!isNested) {
      return {
        x: basePosition.x,
        y: basePosition.y,
        z: basePosition.z || 1,
      };
    }

    const x = Math.max(
      0,
      Math.min(Number(responsiveX) || 0, viewport.width - (Number(responsiveWidth) || 0)),
    );
    const y = Math.max(
      0,
      Math.min(Number(responsiveY) || 0, viewport.height - (Number(responsiveHeight) || 0)),
    );
    return { x, y, z: Number(responsiveZ) || 1 };
  }, [
    isNested,
    basePosition.x,
    basePosition.y,
    basePosition.z,
    responsiveX,
    responsiveY,
    responsiveZ,
    viewport.width,
    viewport.height,
    responsiveWidth,
    responsiveHeight,
  ]);

  const constrainedSize = useMemo(() => {
    const maxWidth = viewport.width - constrainedPosition.x;
    const maxHeight = viewport.height - constrainedPosition.y;
    const width = Math.min(Number(responsiveWidth) || 100, maxWidth);
    const height = Math.min(Number(responsiveHeight) || 50, maxHeight);
    return { width, height };
  }, [
    responsiveWidth,
    responsiveHeight,
    viewport.width,
    viewport.height,
    constrainedPosition,
  ]);

  // Get style properties
  const baseStyle = renderComponent.style || {};
  const responsiveStyle: Record<string, unknown> = { ...baseStyle };

  // Override with responsive style values if they exist
  Object.keys(baseStyle).forEach((styleProperty) => {
    const responsiveValue = getResponsiveProperty(
      component.id,
      `style.${styleProperty}`,
      viewport.id,
    );
    if (responsiveValue !== undefined) {
      responsiveStyle[styleProperty] = responsiveValue;
    }
  });

  const responsivePosition = constrainedPosition;
  const responsiveSize = constrainedSize;

  const handleClick = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      onSelect(component.id, e.ctrlKey || e.metaKey);
    },
    [component.id, onSelect],
  );

  // Hover handlers for tooltip
  const handleMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setHoveredComponent(component.id);
    },
    [component.id, setHoveredComponent],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Only clear if this component is currently hovered
      if (hoveredComponent === component.id) {
        setHoveredComponent(undefined);
      }
    },
    [component.id, hoveredComponent, setHoveredComponent],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Track mouse movement for real-time collaboration cursor updates
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = (e.clientX - rect.left) / scale;
      const relativeY = (e.clientY - rect.top) / scale;

      setLocalMousePosition({ x: relativeX, y: relativeY });

      // Send cursor update for collaboration if component is being hovered
      if (collaboration.isActive && (isHovered || isSelected)) {
        collaboration.updateCursor({
          x: responsivePosition.x + relativeX,
          y: responsivePosition.y + relativeY,
          z: Number(responsivePosition.z) || 1,
        });
      }
    },
    [scale, responsivePosition, collaboration, isHovered, isSelected],
  );

  // Mouse handlers for selection and context menus

  return (
    <>
      <div
        className="absolute"
        style={{
          left: responsivePosition.x,
          top: responsivePosition.y,
          width: responsiveSize.width,
          height: responsiveSize.height,
          zIndex: Number(responsivePosition.z) || 1,
          pointerEvents: 'auto',
        }}
        data-debug-position={`component:${component.id}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* [Resize Removal] Using SelectableComponent instead of ResizableComponent */}
        {/* Resize handles and constraints removed - AI-first architecture */}
        <SelectableComponent
          component={{
            ...renderComponent,
            position: {
              x: responsivePosition.x,
              y: responsivePosition.y,
              z: Number(responsivePosition.z) || 1,
            },
            size: responsiveSize,
            style: responsiveStyle,
          }}
          isSelected={isSelected}
          isHovered={isHovered}
          style={{
            cursor: "pointer",
            width: "100%",
            height: "100%",
          }}
          className={cn(
            component.locked && "cursor-not-allowed opacity-60",
            component.hidden && "opacity-30",
          )}
          onSelect={(_componentId, event) => {
            if (event) {
              handleClick(event);
            }
          }}
          onMouseMove={handleMouseMove}
        >
          <ComponentRenderer
            component={{
            ...renderComponent,
              position: {
                x: responsivePosition.x,
                y: responsivePosition.y,
                z: Number(responsivePosition.z) || 1,
              },
              size: responsiveSize,
            style: responsiveStyle,
          }}
            viewport={activeBreakpoint === "mobile" ? Viewport.MOBILE : activeBreakpoint === "tablet" ? Viewport.TABLET : Viewport.DESKTOP}
            activeColorScheme={activeColorScheme}
          />
        </SelectableComponent>
      </div>

      {/* Viewport-specific indicator */}
      {(isHovered || isSelected) && (
        <div
          className="absolute px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50 flex items-center gap-1 pointer-events-none"
          style={{
            left: responsivePosition.x,
            top: responsivePosition.y - 32,
            // Remove individual scaling - canvas scaling handles this
            // transform: `scale(${scale})`,
            // transformOrigin: "top left",
          }}
        >
          <span className="text-blue-300">{viewport.name}:</span>
          <span>{component.name}</span>
        </div>
      )}
    </>
  );
};

export const ViewportCanvas: React.FC<
  ViewportCanvasProps & { onActivate: () => void }
> = ({
  viewport,
  isActive,
  isVisible,
  scale,
  components,
  selectedComponents,
  hoveredComponent,
  onComponentSelect,
  onCanvasClick,
  onScroll,
  onRegisterRef,
  className,
  spatialIndexingState,
}) => {
  const theme = useOptionalTheme();
  const renderColorScheme = theme?.resolvedTheme;
  // Get actual viewport dimensions based on viewport ID
  const getActualViewportDimensions = () => {
    switch (viewport.id) {
      case 'desktop':
        return { width: 1920, height: 1080 };
      case 'tablet':
        return { width: 768, height: 1024 };
      case 'mobile':
        return { width: 375, height: 667 };
      default:
        return { width: viewport.width || 1920, height: viewport.height || 1080 };
    }
  };

  const baseViewportDimensions = getActualViewportDimensions();

  // Calculate the actual canvas dimensions based on component positions
  // This ensures the editor canvas matches what will be shown in viewer mode
  const actualDimensions = calculateCanvasContentBounds(
    components,
    baseViewportDimensions,
  );

  // Access canvas settings and project from editor store
  const { canvasSettings, lastUpdateTimestamp, project } = useEditorStore();
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const editorCanvasBackgroundStyle = useMemo(
    () => getEditorCanvasBackgroundStyle(canvasSettings.background),
    [canvasSettings.background],
  );
  const normalizedCanvasBackgroundColor = canvasSettings.background.type === "color"
    ? normalizeLegacyCanvasBackgroundColor(canvasSettings.background.color)
    : undefined;

  // Apply canvas background with proper CSS override
  useEffect(() => {
    if (canvasRef.current && normalizedCanvasBackgroundColor) {
      canvasRef.current.style.setProperty(
        "background-color",
        normalizedCanvasBackgroundColor,
        "important",
      );
    }
  }, [normalizedCanvasBackgroundColor]);

  // AI-first design - components added via chat
  const { setState: _setVisualFeedbackState } = useVisualFeedback();

  // AI-first approach for component creation

  // Filter components visible in this viewport with spatial indexing optimization - MUST BE DEFINED BEFORE Pragmatic handlers
  const visibleComponents = useMemo(() => {
    const startTime = performance.now();
    const spatialIndex = spatialIndexingState?.spatialIndex;

    // Base filtering for hidden/deleted components
    let filteredComponents = components.filter(
      (component) => !component.hidden && !component.isDeleted,
    );

    // Enhanced viewport culling with spatial indexing
    if (
      spatialIndex &&
      spatialIndexingState.isInitialized
    ) {
      try {
        // Define viewport bounds with buffer for smooth scrolling
        const viewportBounds: Rectangle = {
          x: 0,
          y: 0,
          width: actualDimensions.width,
          height: actualDimensions.height,
        };

        // Perform viewport culling using spatial indexing
        const cullingResult = spatialIndex.cullByViewport(
          viewportBounds,
          PERFORMANCE_CONFIG.VIEWPORT_CULL_BUFFER,
        );

        // Get component IDs that are visible in viewport
        const visibleIds = new Set(
          cullingResult.visible.map((bounds) => bounds.id),
        );

        // Filter components to only include those visible in viewport OR newly added/loaded components
        // Give new/loaded components a grace period to be properly positioned in spatial index
        filteredComponents = filteredComponents.filter((component) => {
          const isVisibleInSpatialIndex = visibleIds.has(component.id);
          const isRecentlyAdded =
            component.createdAt &&
            Date.now() - (new Date(component.createdAt)).getTime() < 5000; // 5 second grace period

          // Also give grace period to components that were recently loaded from database
          // This handles the case where user switches from viewer to editor and components need to be indexed
          const currentTime = Date.now();
          const lastUpdateTime = lastUpdateTimestamp || 0;
          const isRecentlyLoaded = currentTime - lastUpdateTime < 10000; // 10 second grace period for loaded components

          const shouldInclude =
            isVisibleInSpatialIndex || isRecentlyAdded || isRecentlyLoaded;

          return shouldInclude;
        });

        const duration = performance.now() - startTime;
        // const _cullingRatio =
        //   (cullingResult.culled.length /
        //     (cullingResult.visible.length + cullingResult.culled.length)) *
        //   100;

        // Throttle culling performance logs to reduce console spam
        const now = Date.now();
        const lastLog = cullingLogTimestamps.get(viewport.id) ?? 0;

        if (now - lastLog > 1000) {
          // Max once per second per viewport
          cullingLogTimestamps.set(viewport.id, now);
        }

        if (duration > 2) {
          const lastWarn = cullingWarnTimestamps.get(viewport.id) ?? 0;
          if (now - lastWarn > 3000) {
            // Max once per 3 seconds
            logger.warn(
              `⚠️ Slow viewport culling detected: ${duration.toFixed(2)}ms`,
            );
            cullingWarnTimestamps.set(viewport.id, now);
          }
        }
      } catch (error) {
        logger.error("Error during viewport culling:", error);
        // Fallback to basic filtering
      }
    }

    return filteredComponents;
  }, [
    components,
    viewport.id,
    actualDimensions.width,
    actualDimensions.height,
    spatialIndexingState?.spatialIndex,
    spatialIndexingState?.isInitialized,
    lastUpdateTimestamp,
  ]);

  // AI-first approach - components added through chat
  // This application now uses an AI-first approach for component manipulation
  // Components are added and positioned through the AI interface

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && onCanvasClick) {
        onCanvasClick(e);
      }
    },
    [onCanvasClick],
  );

  if (!isVisible) return null;

  return (
    <Card
      className={cn(
        "flex flex-col min-h-[400px] overflow-hidden",
        "rounded-lg border-0 bg-background shadow-none",
        className,
      )}
    >
      {/* Canvas Header */}
      <div
        className={cn(
          "flex items-center justify-between px-4 py-2.5 border-b",
          "transition-all duration-200"
        )}
        style={{
          backgroundColor: isActive ? 'hsl(var(--primary) / 0.03)' : 'transparent',
          borderColor: isActive ? 'hsl(var(--primary) / 0.19)' : 'hsl(var(--border))',
        }}
      >
        <div className="flex items-center gap-3">
          {/* Primary accent bar */}
          <div className="w-1 h-5 rounded-full bg-primary" />
          {project && (
            <span
              className="text-sm font-medium tracking-tight text-muted-foreground"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              {project.name || `Project: ${project.id?.substring(0, 8)}...`}
            </span>
          )}
        </div>
      </div>

      {/* Canvas Area */}
      <div
        ref={(element) => {
          canvasRef.current = element;
          onRegisterRef?.(element);
        }}
        className={cn(
          "flex-1 relative overflow-auto",
          "transition-all duration-300 ease-out",
          // Professional canvas background
          "bg-muted/50",
          // Clean empty state styling
          visibleComponents.length === 0 &&
            "bg-gradient-to-br from-muted/30 to-muted/50",
        )}
        style={{
          // Container fits the available space with scrolling
          width: '100%',
          height: '100%',
          // Enable proper scrolling
          position: 'relative',
          overflow: 'auto',
        }}
        onScroll={onScroll}
      >
        {/* Inner wrapper that provides proper spacing for scaled content */}
        <div
          style={{
            // Reserve space for the scaled canvas
            width: actualDimensions.width * scale + 40,
            height: actualDimensions.height * scale + 40,
            padding: '20px',
            position: 'relative',
            minWidth: '100%',
            minHeight: '100%',
          }}
        >
          <div
            ref={canvasRef}
            className="relative cursor-default"
            data-canvas-bg-color={normalizedCanvasBackgroundColor}
            style={{
              // Use actual viewport dimensions for the editable area
              width: actualDimensions.width,
              height: actualDimensions.height,
              // Remove margin as flex container handles centering
              margin: 0,
              minHeight: actualDimensions.height,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            // Add subtle professional styling
            borderRadius: scale < 0.8 ? '8px' : '4px',
            boxShadow: scale < 0.8 ? '0 4px 12px rgba(0,0,0,0.08)' : '0 2px 4px rgba(0,0,0,0.04)',
            // Canvas background styling
            ...editorCanvasBackgroundStyle,
            // Canvas layout styling - no padding to maintain exact dimensions
            // padding: `${canvasSettings.layout.padding.top}px ${
            //   canvasSettings.layout.padding.right
            // }px ${canvasSettings.layout.padding.bottom}px ${
            //   canvasSettings.layout.padding.left
            // }px`,
            padding: 0,
            overflow: canvasSettings.layout.overflow,
          }}
          onClick={handleCanvasClick}
        >
          {/* Invisible background to catch clicks */}
          <div
            className="absolute inset-0 cursor-default"
            onClick={handleCanvasClick}
            style={{ zIndex: 0 }}
          />

          {/* Grid that covers entire canvas */}
          {canvasSettings.grid.enabled && (
            <>
              {/* Minor grid lines */}
              <div
                className="absolute pointer-events-none"
                style={{
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                        backgroundImage: `
                          linear-gradient(to right, ${canvasSettings.grid.color} 1px, transparent 1px),
                          linear-gradient(to bottom, ${canvasSettings.grid.color} 1px, transparent 1px)
                        `,
                  backgroundSize: `${canvasSettings.grid.size}px ${canvasSettings.grid.size}px`,
                  opacity: canvasSettings.grid.opacity,
                  zIndex: 1,
                }}
              />

              {/* Major grid lines */}
              {canvasSettings.grid.majorLines.enabled && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `
                      linear-gradient(to right, ${canvasSettings.grid.majorLines.color} 1px, transparent 1px),
                      linear-gradient(to bottom, ${canvasSettings.grid.majorLines.color} 1px, transparent 1px)
                    `,
                    backgroundSize: `${canvasSettings.grid.size * canvasSettings.grid.majorLines.interval}px ${canvasSettings.grid.size * canvasSettings.grid.majorLines.interval}px`,
                    opacity: canvasSettings.grid.majorLines.opacity,
                    zIndex: 2,
                  }}
                />
              )}
            </>
          )}

          {/* Empty state */}
          {visibleComponents.length === 0 && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ zIndex: 2 }}
            >
              <div className="text-center space-y-4 p-8">
                <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-foreground/80">
                    Building Your {viewport.name} View
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Use the AI chat to add components and build your layout
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* Render components - only render root level components (those without parentId) */}
          <div className="relative" style={{ zIndex: 10 }}>
            {(() => {
              const rootComponents = visibleComponents.filter(component => !component.parentId);

              return rootComponents.map((component) => (
                <ResponsiveComponent
                  key={component.id}
                  component={component}
                  viewport={viewport}
                  isSelected={selectedComponents.includes(component.id)}
                  isHovered={hoveredComponent === component.id}
                  onSelect={onComponentSelect}
                  scale={scale}
                  activeColorScheme={renderColorScheme}
                />
              ));
            })()}
          </div>

          {/* Sketch Overlay for Plan Mode */}
          <SketchOverlay
            width={actualDimensions.width}
            height={actualDimensions.height}
            className="sketch-overlay"
          />

          {/* Interaction Preview: show arrows when a single selected component has interactions */}
          {(() => {
            if (selectedComponents.length !== 1) return null;
            const selectedComp = visibleComponents.find(c => c.id === selectedComponents[0]);
            if (!selectedComp?.interactions?.length) return null;

            // Build approximate position map from DOM elements
            const posMap = new Map<string, { x: number; y: number; width: number; height: number }>();
            const canvasEl = canvasRef.current;
            if (canvasEl) {
              const canvasRect = canvasEl.getBoundingClientRect();
              canvasEl.querySelectorAll('[data-component-id]').forEach(el => {
                const compId = el.getAttribute('data-component-id');
                if (compId) {
                  const rect = el.getBoundingClientRect();
                  posMap.set(compId, {
                    x: rect.left - canvasRect.left,
                    y: rect.top - canvasRect.top,
                    width: rect.width,
                    height: rect.height,
                  });
                }
              });
            }

            return (
              <InteractionPreview
                interactions={selectedComp.interactions}
                sourceComponentId={selectedComp.id}
                componentPositions={posMap}
              />
            );
          })()}

        </div>
        </div>{/* Close inner wrapper */}
      </div>
    </Card>
  );
};

export const MultiViewportCanvas: React.FC<MultiViewportCanvasProps> = ({
  className,
}) => {
  const theme = useOptionalTheme();
  const renderColorScheme = theme?.resolvedTheme;
  // Enable real-time CSS injection and updates
  useRealtimeCSS();
  useResponsiveCSSTrigger();

  // Session management for user interactions
  const { extendSession } = useAuthSession();

  // Week 3: Enable selection keyboard shortcuts (Delete, Copy, Escape, Select All)
  useSelectionKeyboardShortcuts({
    enabled: true,
    onShortcutTriggered: (shortcut, selectedCount) => {
      logger.debug("Selection keyboard shortcut triggered", {
        shortcut,
        selectedCount,
      });
      // Extend session on keyboard interaction
      extendSession();
    },
  });


  // Spatial Indexing State Management
  const [spatialIndexingState, setSpatialIndexingState] =
    useState<SpatialIndexingState>({
      spatialIndex: null,
      registeredComponents: new Map(),
      performanceMetrics: {
        renderTime: 0,
        frameDrops: 0,
        updateFrequency: 0,
        memoryUsage: 0,
        optimizationsApplied: [],
      },
      isInitialized: false,
    });

  const {
    availableViewports,
    visibleViewports,
    previewMode,
    activeViewport,
    syncScrolling,
    syncSelection,
    setActiveViewport,
    updateViewportState: _updateViewportState,
  } = useResponsiveStore();

  // const { viewportStates } = useViewportStates(); // Currently unused

  const {
    components,
    selectedComponents,
    hoveredComponent,
    zoom,
    canvasSettings: _canvasSettings,
    selectComponent,
    selectMultipleComponents,
    clearSelection: _clearSelection,
    addComponent,
    moveComponent,
    selectCanvas,
    lastUpdateTimestamp: _lastUpdateTimestamp,
    project: _project,
  } = useEditorStore();

  // Filter viewports that should be displayed (with deduplication safety)
  const displayedViewports = useMemo(() => {
    const filtered = availableViewports.filter((vp) =>
      visibleViewports.includes(vp.id),
    );
    // Safety: Deduplicate by viewport ID to prevent React key errors
    const seen = new Set<string>();
    const deduplicated = filtered.filter((vp) => {
      if (seen.has(vp.id)) {
        logger.warn("Duplicate viewport ID detected in multi-viewport canvas", {
          viewportId: vp.id,
        });
        return false;
      }
      seen.add(vp.id);
      return true;
    });
    return deduplicated;
  }, [availableViewports, visibleViewports]);

  // Calculate grid layout based on preview mode and number of viewports
  const gridColumns = useMemo(() => {
    switch (previewMode) {
      case "single":
        return 1;
      case "split":
        return Math.min(2, displayedViewports.length);
      case "multi":
        return Math.min(3, Math.ceil(Math.sqrt(displayedViewports.length)));
      default:
        return 1;
    }
  }, [previewMode, displayedViewports.length]);

  // Initialize Spatial Indexing System for performance optimization
  // NOTE: Spatial indexing has been removed - TerraMatrix is now AI-chat-only with flex layout
  useEffect(() => {
    // Spatial indexing removed - mark as initialized without creating spatial index
    if (displayedViewports.length > 0 && !spatialIndexingState.isInitialized) {
      setSpatialIndexingState((prev) => ({
        ...prev,
        spatialIndex: null,
        isInitialized: true, // Mark as initialized so other effects can run
      }));
    }
  }, [displayedViewports, spatialIndexingState.isInitialized]);

  // Component Registration with Spatial Indexing
  const {
    spatialIndex: currentSpatialIndex,
    isInitialized: isSpatialIndexInitialized,
    registeredComponents,
  } = spatialIndexingState;

  useEffect(() => {
    const spatialIndex = currentSpatialIndex;
    if (!spatialIndex || !isSpatialIndexInitialized) return undefined;

    const registerComponents = async () => {
      const startTime = performance.now();
      try {
        const primaryViewport = displayedViewports[0];
        if (!primaryViewport) {
          return;
        }

        const { getResponsiveProperty, availableViewports } =
          useResponsiveStore.getState();
        const baseViewport =
          availableViewports.find((vp) => vp.id === "desktop") || primaryViewport;

        for (const component of components) {
          if (component.hidden || component.isDeleted) continue;

          const basePosition: Position = {
            x: toFiniteNumber(
              getResponsiveProperty(component.id, "position.x", primaryViewport.id),
              component.position?.x ?? 0,
            ),
            y: toFiniteNumber(
              getResponsiveProperty(component.id, "position.y", primaryViewport.id),
              component.position?.y ?? 0,
            ),
            z: toFiniteNumber(
              getResponsiveProperty(component.id, "position.z", primaryViewport.id),
              component.position?.z ?? 1,
            ),
          };

          const baseSize = {
            width: toFiniteNumber(
              getResponsiveProperty(component.id, "size.width", primaryViewport.id),
              toFiniteNumber(component.size?.width, 100),
            ),
            height: toFiniteNumber(
              getResponsiveProperty(component.id, "size.height", primaryViewport.id),
              toFiniteNumber(component.size?.height, 50),
            ),
          };

          if (baseSize.width <= 0 || baseSize.height <= 0) {
            continue;
          }

          const responsivePosition = calculateResponsivePosition({
            basePosition,
            baseViewport,
            targetViewport: primaryViewport,
            containerDimensions: {
              width: primaryViewport.width,
              height: primaryViewport.height,
            },
          });

          const responsiveSize = calculateResponsiveSize(
            baseSize,
            baseViewport,
            primaryViewport,
          );

          const { position, size } = applyResponsiveConstraints(
            responsivePosition.position,
            { width: responsiveSize.width, height: responsiveSize.height },
            primaryViewport,
            {
              minWidth: 1,
              minHeight: 1,
            },
          );

          const componentBounds: ComponentBounds = {
            id: component.id,
            x: Math.max(0, position.x),
            y: Math.max(0, position.y),
            z: position.z ?? 1,
            width: Math.max(1, toFiniteNumber(size.width, 1)),
            height: Math.max(1, toFiniteNumber(size.height, 1)),
            type: component.type || "unknown",
            lastUpdated: Date.now(),
          };

          // Check if component is already registered with same bounds
          const existing = registeredComponents.get(component.id);
          const boundsChanged =
            !existing ||
            existing.x !== componentBounds.x ||
            existing.y !== componentBounds.y ||
            existing.width !== componentBounds.width ||
            existing.height !== componentBounds.height;

          if (boundsChanged) {
            // Use updateComponent for existing components, insert for new ones
            if (existing) {
              spatialIndex.updateComponent(component.id, componentBounds);
            } else {
              spatialIndex.insert(componentBounds);
            }

            setSpatialIndexingState((prev) => ({
              ...prev,
              registeredComponents: new Map(
                prev.registeredComponents.set(component.id, componentBounds),
              ),
            }));

          }
        }

        // Remove components that no longer exist
        const existingIds = new Set(components.map((c) => c.id));
        for (const [componentId] of registeredComponents) {
          if (!existingIds.has(componentId)) {
            spatialIndex.remove(componentId);
            setSpatialIndexingState((prev) => {
              const newRegistered = new Map(prev.registeredComponents);
              newRegistered.delete(componentId);
              return { ...prev, registeredComponents: newRegistered };
            });
          }
        }

        // Update performance metrics
        const updateDuration = performance.now() - startTime;
        setSpatialIndexingState((prev) => ({
          ...prev,
          performanceMetrics: {
            ...prev.performanceMetrics,
            renderTime: updateDuration,
            updateFrequency: 60, // Assume 60fps target
            memoryUsage: spatialIndex.size() * 200, // Rough estimate
            optimizationsApplied:
              updateDuration > 5
                ? [
                    ...prev.performanceMetrics.optimizationsApplied,
                    "registration-optimization",
                  ]
                : prev.performanceMetrics.optimizationsApplied,
          },
        }));
      } catch (error) {
        logger.error(
          "Failed to register components with spatial indexing:",
          error,
        );
      }
    };

    registerComponents();
  }, [
    components,
    displayedViewports,
    isSpatialIndexInitialized,
    currentSpatialIndex,
    registeredComponents,
  ]);

  const handleComponentSelect = useCallback(
    (componentId: string, ctrlKey?: boolean) => {
      // Extend session on user interaction
      extendSession();

      // Spatial indexing optimization for selection
      const spatialIndex = spatialIndexingState.spatialIndex;
      if (spatialIndex && spatialIndexingState.isInitialized) {
        // Performance timing temporarily disabled for this optimization

        try {
          // Get component bounds from spatial index for fast lookup
          const componentBounds = spatialIndex.getBounds(componentId);

          if (componentBounds) {
            // Find nearby components for potential multi-selection suggestions
            // Find nearby components for potential multi-selection suggestions (for future use)
            // const nearbyComponents = spatialIndexingState.spatialIndex.query({
            //   x: componentBounds.x - 50,
            //   y: componentBounds.y - 50,
            //   width: componentBounds.width + 100,
            //   height: componentBounds.height + 100,
            // });

            // const duration = performance.now() - startTime;
          }
        } catch (error) {
          logger.error(
            "Error in spatial-enhanced component selection:",
            error,
          );
        }
      }

      if (syncSelection) {
        // Apply selection to all viewports
        if (ctrlKey) {
          const isSelected = selectedComponents.includes(componentId);
          if (isSelected) {
            selectMultipleComponents(
              selectedComponents.filter((id) => id !== componentId),
            );
          } else {
            selectMultipleComponents([...selectedComponents, componentId]);
          }
        } else {
          selectComponent(componentId);
        }
      } else {
        // Apply selection only to current viewport
        selectComponent(componentId);
      }
    },
    [
      selectedComponents,
      selectComponent,
      selectMultipleComponents,
      syncSelection,
      extendSession,
      spatialIndexingState,
    ],
  );

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      // Select canvas when clicking directly on it, not child elements
      if (e.target === e.currentTarget) {
        selectCanvas();
      }
    },
    [selectCanvas],
  );

  const _handleComponentAdd = useCallback(
    (componentType: ComponentType, _position: Position, defaultData?: DefaultComponentData) => {
      // Extend session on user interaction
      extendSession();

      // 🚀 FIX: Use React.startTransition for better batching
      React.startTransition(() => {
        // Get fresh component data from store to avoid stale closures
        const currentComponents = useEditorStore.getState().components;

        // Get default properties or provide fallbacks
        const defaultProperties =
          defaultData?.defaultProperties ||
          getDefaultPropertiesForType(componentType);
        const defaultSize = defaultData?.defaultSize || {
          width: 200,
          height: 100,
        };
        const defaultStyle = defaultData?.defaultStyle || {};

        // 🚀 FIX: Simplify position logic to avoid delays
        let validPosition: Position;

        // Force structured auto-positioning

        // Always use auto-positioning for structured layout
        const yOffset = currentComponents.length * 80; // Stack components vertically with 80px spacing (reduced from 120px)
        validPosition = {
          x: 20, // More top-left positioning (reduced from 40px)
          y: 20 + yOffset, // Start closer to top-left and stack vertically (reduced from 40px)
          z: currentComponents.length,
        };

        // Create new component with the addComponent store action
        const newComponentData: Omit<Component, "id" | "createdAt" | "updatedAt"> = {
          type: componentType,
          position: validPosition,
          responsivePosition: {
            strategy: 'flex', // Flex-only architecture (December 2025)
            constraints: undefined,
            responsiveSize: undefined,
            breakpoints: undefined,
          },
          size: defaultSize,
          properties: defaultProperties,
          style: defaultStyle,
          name: `${componentType.charAt(0).toUpperCase() + componentType.slice(1)} Component`,
          projectId: "",
          pageId: "", // Required field - will be set by the store
          parentId: undefined,
          children: [],
          order: 0,
          locked: false,
          hidden: false,
          version: 1,
          createdBy: "",
          lastModifiedBy: "",
          lastModifiedAt: new Date(),
          isDeleted: false,
        };

        // 🚀 FIX: Add component immediately
        addComponent(newComponentData);
      });
    },
    [addComponent, extendSession],
  );

  const handleComponentMove = useCallback(
    (componentId: string, position: Position) => {
      // Extend session on user interaction
      extendSession();

      logger.debug("Moving component in active viewport", {
        componentId,
        position,
        activeViewport,
      });

      // 🚀 CRITICAL FIX: Direct approach - update the base component position
      // The responsive property system seems to have synchronization issues
      // So let's update the base position directly which will trigger re-render immediately

      moveComponent(componentId, position);

      logger.debug("Updated base component position", {
        componentId,
        position,
      });

      // Also update responsive properties for consistency across viewports
      const { setResponsiveProperty } = useResponsiveStore.getState();
      setResponsiveProperty(componentId, "position.x", activeViewport, position.x);
      setResponsiveProperty(componentId, "position.y", activeViewport, position.y);
      setResponsiveProperty(componentId, "position.z", activeViewport, position.z);

      // Force a small delay to ensure state propagation, then verify
      setTimeout(() => {
        const updatedComponent = components.find(c => c.id === componentId);
        const { getResponsiveProperty } = useResponsiveStore.getState();
        const newResponsiveX = getResponsiveProperty(componentId, "position.x", activeViewport);
        const newResponsiveY = getResponsiveProperty(componentId, "position.y", activeViewport);

        logger.debug("Component position verification after move", {
          componentId,
          basePosition: updatedComponent?.position,
          responsivePosition: { x: newResponsiveX, y: newResponsiveY },
          viewport: activeViewport,
          requestedPosition: position
        });
      }, 100);
    },
    [moveComponent, extendSession, components, activeViewport],
  );

  const _handleViewportActivate = useCallback(
    (viewportId: string) => {
      // Extend session when switching viewports
      extendSession();
      setActiveViewport(viewportId);

      // Additional viewport activation logic could go here
      // - Update responsive settings
      // - Sync component visibility
      // - Update canvas zoom for specific viewport
    },
    [setActiveViewport, extendSession],
  );

  // Track refs for all viewport canvases for scrolling sync
  const viewportRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Synchronized scrolling handler
  const _handleScroll = useCallback(
    (sourceViewportId: string, e: React.UIEvent<HTMLDivElement>) => {
      if (!syncScrolling) return;

      const sourceElement = e.currentTarget;
      const scrollTop = sourceElement.scrollTop;
      const scrollLeft = sourceElement.scrollLeft;

      // Sync scroll positions across all other visible viewport canvases
      visibleViewports.forEach((viewportId) => {
        if (viewportId !== sourceViewportId) {
          const targetElement = viewportRefs.current.get(viewportId);
          if (targetElement) {
            // Temporarily disable sync to avoid infinite loop
            const wasScrolling = targetElement.dataset.syncing === "true";
            if (!wasScrolling) {
              targetElement.dataset.syncing = "true";
              targetElement.scrollTop = scrollTop;
              targetElement.scrollLeft = scrollLeft;
              requestAnimationFrame(() => {
                targetElement.dataset.syncing = "false";
              });
            }
          }
        }
      });
    },
    [syncScrolling, visibleViewports],
  );

  const _registerViewportRef = useCallback(
    (viewportId: string, element: HTMLDivElement | null) => {
      if (element) {
        viewportRefs.current.set(viewportId, element);
      } else {
        viewportRefs.current.delete(viewportId);
      }
    },
    [],
  );

  // Spatial Query Integration Hook (available for future use)
  /* const _spatialQueries = useMemo(() => {
    if (
      !spatialIndexingState.spatialIndex ||
      !spatialIndexingState.isInitialized
    ) {
      return {
        queryComponentsInArea: () => [],
        findNearbyComponents: () => [],
        findComponentsOfType: () => [],
        getComponentsInRadius: () => [],
        performanceQuery: () => ({ components: [], duration: 0 }),
      };
    }

    const spatialIndex = spatialIndexingState.spatialIndex;

    return {
      // Query components within a rectangular area
      queryComponentsInArea: (bounds: Rectangle, maxResults?: number) => {
        const startTime = performance.now();
        const results = spatialIndex.queryRange({
          bounds,
          maxResults,
          sorted: true,
        });
        const duration = performance.now() - startTime;

        if (duration > 5) {
          logger.warn(
            `⚠️ Slow spatial query: ${duration.toFixed(2)}ms for area query`,
          );
        }

        return results
          .map((bounds) => components.find((c) => c.id === bounds.id))
          .filter(Boolean);
      },

      // Find components near a specific point
      findNearbyComponents: (center: Position3D, radius: number = 50) => {
        // const startTime = performance.now();
        const bounds: Rectangle = {
          x: center.x - radius,
          y: center.y - radius,
          width: radius * 2,
          height: radius * 2,
        };

        const results = spatialIndex.query(bounds);
        const nearbyComponents = results
          .map((bounds) => {
            const component = components.find((c) => c.id === bounds.id);
            if (!component) return null;

            // Calculate actual distance
            const distance = Math.sqrt(
              Math.pow(bounds.x + bounds.width / 2 - center.x, 2) +
                Math.pow(bounds.y + bounds.height / 2 - center.y, 2),
            );

            return distance <= radius ? { component, distance } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance)
          .map((item) => item.component);

        // const duration = performance.now() - startTime;

        return nearbyComponents;
      },

      // Find components of specific type(s)
      findComponentsOfType: (types: string | string[]) => {
        // const startTime = performance.now();
        const typeArray = Array.isArray(types) ? types : [types];

        const allComponents = spatialIndex.getAllComponents();
        const filteredComponents = allComponents
          .filter((bounds) => typeArray.includes(bounds.type))
          .map((bounds) => components.find((c) => c.id === bounds.id))
          .filter(Boolean);

        // const duration = performance.now() - startTime;

        return filteredComponents;
      },

      // Get components within a circular radius
      getComponentsInRadius: (center: Position3D, radius: number) => {
        // Use the findNearbyComponents implementation directly to avoid circular reference
        // const startTime = performance.now();
        const bounds: Rectangle = {
          x: center.x - radius,
          y: center.y - radius,
          width: radius * 2,
          height: radius * 2,
        };

        const results = spatialIndex.query(bounds);
        const nearbyComponents = results
          .map((bounds) => {
            const component = components.find((c) => c.id === bounds.id);
            if (!component) return null;

            // Calculate actual distance
            const distance = Math.sqrt(
              Math.pow(bounds.x + bounds.width / 2 - center.x, 2) +
                Math.pow(bounds.y + bounds.height / 2 - center.y, 2),
            );

            return distance <= radius ? { component, distance } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance)
          .map((item) => item.component);

        return nearbyComponents;
      },

      // Performance-monitored query
      performanceQuery: (bounds: Rectangle) => {
        const startTime = performance.now();
        const results = spatialIndex.query(bounds);
        const duration = performance.now() - startTime;

        return {
          components: results
            .map((bounds) => components.find((c) => c.id === bounds.id))
            .filter(Boolean),
          duration,
          performanceRating:
            duration < 1
              ? "excellent"
              : duration < 3
                ? "good"
                : duration < 5
                  ? "acceptable"
                  : "poor",
        };
      },
    };
  }, [spatialIndexingState, components]); */

  // Expose spatial queries for external use (available if needed)
  // const exposeSpatialQueries = useCallback(
  //   () => spatialQueries,
  //   [spatialQueries],
  // );

  // Performance Monitoring and Auto-Optimization - THROTTLED to prevent continuous re-renders
  useEffect(() => {
    const spatialIndex = spatialIndexingState.spatialIndex;
    if (!spatialIndex || !spatialIndexingState.isInitialized)
      return undefined;

    // Use a longer interval and throttle updates to prevent continuous re-renders
    const optimizationInterval = setInterval(() => {
      try {
        // Get current performance metrics
        const metrics = spatialIndex.getMetrics();
        const { renderTime, memoryUsage } = metrics;

        // Auto-optimization triggers
        const needsOptimization =
          renderTime > 10 || // Slower than 10ms
          memoryUsage > 50000; // More than 50KB memory

        if (needsOptimization) {
          // Apply optimizations
          const optimizations: string[] = [];

          // Clear caches if memory usage is high
          if (memoryUsage > 50000) {
            // spatialIndexingState.spatialIndex.clearCaches(); // Would need this method
            optimizations.push("cache-clear");
          }

          // Reduce update frequency if performance is poor
          if (renderTime > 15) {
            // Could throttle updates
            optimizations.push("update-throttling");
          }

          // Only update state if there are actual optimizations to apply
          if (optimizations.length > 0) {
            setSpatialIndexingState((prev) => ({
              ...prev,
              performanceMetrics: {
                ...prev.performanceMetrics,
                optimizationsApplied: [
                  ...prev.performanceMetrics.optimizationsApplied,
                  ...optimizations,
                ],
              },
            }));
          }
        }

        // Only update performance metrics if there's a significant change
        const hasSignificantChange =
          Math.abs(
            metrics.renderTime -
              spatialIndexingState.performanceMetrics.renderTime,
          ) > 1 ||
          Math.abs(
            metrics.memoryUsage -
              spatialIndexingState.performanceMetrics.memoryUsage,
          ) > 1000;

        if (hasSignificantChange) {
          setSpatialIndexingState((prev) => ({
            ...prev,
            performanceMetrics: {
              ...metrics,
              optimizationsApplied:
                prev.performanceMetrics.optimizationsApplied,
            },
          }));
        }
      } catch (error) {
        logger.error("Error in performance monitoring:", error);
      }
    }, 10000); // Check every 10 seconds instead of 2 seconds to reduce re-renders

    return () => clearInterval(optimizationInterval);
  }, [
    spatialIndexingState.spatialIndex,
    spatialIndexingState.isInitialized,
    spatialIndexingState.performanceMetrics.renderTime,
    spatialIndexingState.performanceMetrics.memoryUsage,
  ]);

  // Performance benchmarking for development
  const benchmarkSpatialPerformance = useCallback(async () => {
    const spatialIndex = spatialIndexingState.spatialIndex;
    if (!spatialIndex) return;

    const startTime = performance.now();
    const testQueries = 1000;
    const viewportBounds: Rectangle = {
      x: 0,
      y: 0,
      width: displayedViewports[0]?.width || 1920,
      height: displayedViewports[0]?.height || 1080,
    };

    // Perform multiple queries to test performance
    for (let i = 0; i < testQueries; i++) {
      const randomBounds: Rectangle = {
        x: Math.random() * viewportBounds.width,
        y: Math.random() * viewportBounds.height,
        width: 100 + Math.random() * 200,
        height: 50 + Math.random() * 150,
      };

      spatialIndex.query(randomBounds);
    }

    const duration = performance.now() - startTime;
    const avgQueryTime = duration / testQueries;

    return {
      avgQueryTime,
      queriesPerSecond: Math.round(1000 / avgQueryTime),
      performanceRating: avgQueryTime < 5 ? "excellent" : "needs-optimization",
    };
  }, [spatialIndexingState.spatialIndex, displayedViewports]);

  // Expose benchmark function for external use
  window.benchmarkSpatialPerformance = benchmarkSpatialPerformance;

  // 1000+ Components Performance Test Simulation
  const testLargeScalePerformance = useCallback(async () => {
    const spatialIndex = spatialIndexingState.spatialIndex;
    if (!spatialIndex) {
      return;
    }

    const testComponentCounts = [100, 500, 1000, 2000, 5000];
    const results: LargeScalePerformanceResult[] = [];

    for (const componentCount of testComponentCounts) {
      const testStartTime = performance.now();

      // Generate test components
      const testComponents: ComponentBounds[] = [];
      const canvasWidth = displayedViewports[0]?.width || 1920;
      const canvasHeight = displayedViewports[0]?.height || 1080;

      for (let i = 0; i < componentCount; i++) {
        testComponents.push({
          id: `test-component-${i}`,
          x: Math.random() * (canvasWidth - 100),
          y: Math.random() * (canvasHeight - 100),
          z: Math.floor(Math.random() * 10),
          width: 50 + Math.random() * 150,
          height: 30 + Math.random() * 100,
          type: ["button", "text", "image", "container", "form"][
            Math.floor(Math.random() * 5)
          ],
          lastUpdated: Date.now(),
        });
      }

      // Clear existing test components and insert new ones
      spatialIndex.clear();

      const insertStartTime = performance.now();
      testComponents.forEach((component) => {
        spatialIndex.insert(component);
      });
      const insertDuration = performance.now() - insertStartTime;

      // Test spatial queries
      const queryStartTime = performance.now();
      const numQueries = 100;

      for (let i = 0; i < numQueries; i++) {
        // Random query bounds
        const queryBounds: Rectangle = {
          x: Math.random() * canvasWidth * 0.8,
          y: Math.random() * canvasHeight * 0.8,
          width: 200 + Math.random() * 300,
          height: 150 + Math.random() * 250,
        };

        spatialIndex.query(queryBounds);
      }
      const queryDuration = performance.now() - queryStartTime;
      const avgQueryTime = queryDuration / numQueries;

      // Test collision detection
      const collisionStartTime = performance.now();
      const numCollisionTests = 50;

      for (let i = 0; i < numCollisionTests; i++) {
        const randomComponent =
          testComponents[Math.floor(Math.random() * testComponents.length)];
        spatialIndex.findCollisions(randomComponent);
      }
      const collisionDuration = performance.now() - collisionStartTime;
      const avgCollisionTime = collisionDuration / numCollisionTests;

      // Test viewport culling
      const cullingStartTime = performance.now();
      const viewportBounds: Rectangle = {
        x: 0,
        y: 0,
        width: canvasWidth,
        height: canvasHeight,
      };
      const cullingResult = spatialIndex.cullByViewport(viewportBounds);
      const cullingDuration = performance.now() - cullingStartTime;
      const cullingRatio =
        cullingResult.culled.length /
        (cullingResult.visible.length + cullingResult.culled.length);

      const totalTestDuration = performance.now() - testStartTime;

      const testResult = {
        componentCount,
        insertDuration: insertDuration.toFixed(2),
        avgQueryTime: avgQueryTime.toFixed(3),
        avgCollisionTime: avgCollisionTime.toFixed(3),
        cullingDuration: cullingDuration.toFixed(2),
        cullingRatio: (cullingRatio * 100).toFixed(1),
        totalDuration: totalTestDuration.toFixed(2),
        performanceRating:
          avgQueryTime < 5
            ? "🚀 Excellent"
            : avgQueryTime < 10
              ? "✅ Good"
              : "⚠️ Needs optimization",
        targetAchieved: avgQueryTime < 5,
        memorySizeEstimate: `${((componentCount * 200) / 1024).toFixed(1)}KB`,
      };

      results.push(testResult);
    }

    // Clear test data
    spatialIndex.clear();

    return results;
  }, [spatialIndexingState.spatialIndex, displayedViewports]);

  // Expose test function for external use
  window.testLargeScalePerformance = testLargeScalePerformance;

  return (
    <div className={cn("h-full p-4", className)}>
      {displayedViewports.length === 0 ? (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <div className="text-center">
            <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No viewports visible</h3>
            <p className="text-sm">
              Select viewports from the Viewport Manager to start designing
            </p>
          </div>
        </div>
      ) : (
        <div
          className="grid gap-4 h-full"
          style={{
            gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          }}
        >
          {displayedViewports.map((viewport) => (
            <ResponsiveEditorCanvas
              key={viewport.id}
              viewport={viewport}
              isActive={activeViewport === viewport.id}
              isVisible={visibleViewports.includes(viewport.id)}
              scale={zoom}
              components={components}
              selectedComponents={selectedComponents}
              hoveredComponent={hoveredComponent}
              onComponentSelect={handleComponentSelect}
              onCanvasClick={handleCanvasClick}
              onComponentUpdate={(id, updates) => {
                if (updates.position) {
                  handleComponentMove(id, updates.position);
                }
                if (updates.size) {
                  const component = components.find((c) => c.id === id);
                  if (component) {
                    // Use optional chaining and provide defaults for deprecated position property
                    const basePosition = component.position ?? { x: 0, y: 0, z: 1 };
                    const newPosition: Position = {
                      x: updates.position?.x ?? basePosition.x,
                      y: updates.position?.y ?? basePosition.y,
                      z: updates.position?.z ?? basePosition.z ?? 1,
                    };
                    moveComponent(id, newPosition);
                  }
                }
              }}
              activeColorScheme={renderColorScheme}
              spatialIndex={spatialIndexingState.spatialIndex}
              className={cn(
                "transition-all duration-200",
                previewMode === "single" && "col-span-full",
              )}
            />
          ))}
        </div>
      )}

      {/* Enhanced Performance Indicator with Spatial Indexing Metrics */}
      {displayedViewports.length > 1 && (
        <div className="fixed bottom-4 right-4 space-y-2">
          <div className="px-3 py-1 bg-background/90 backdrop-blur-sm border rounded text-xs text-muted-foreground">
            {displayedViewports.length} viewport
            {displayedViewports.length !== 1 ? "s" : ""} • Sync:{" "}
            {syncScrolling ? "On" : "Off"}
          </div>

          {/* Spatial Indexing Performance Metrics */}
          {spatialIndexingState.isInitialized && (
            <div className="px-3 py-2 bg-background/95 backdrop-blur-sm border rounded text-xs space-y-1">
              <div className="font-semibold text-primary">
                Spatial Performance
              </div>
              <div className="flex justify-between">
                <span>Components:</span>
                <span
                  className={cn(
                    spatialIndexingState.registeredComponents.size > 1000
                      ? "text-green-600"
                      : "text-muted-foreground",
                  )}
                >
                  {spatialIndexingState.registeredComponents.size}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Render Time:</span>
                <span
                  className={cn(
                    spatialIndexingState.performanceMetrics.renderTime < 5
                      ? "text-green-600"
                      : spatialIndexingState.performanceMetrics.renderTime < 10
                        ? "text-yellow-600"
                        : "text-red-600",
                  )}
                >
                  {spatialIndexingState.performanceMetrics.renderTime.toFixed(
                    1,
                  )}
                  ms
                </span>
              </div>
              <div className="flex justify-between">
                <span>Memory:</span>
                <span className="text-muted-foreground">
                  {(
                    spatialIndexingState.performanceMetrics.memoryUsage / 1024
                  ).toFixed(1)}
                  KB
                </span>
              </div>
              {spatialIndexingState.performanceMetrics.optimizationsApplied
                .length > 0 && (
                <div className="flex justify-between">
                  <span>Optimizations:</span>
                  <span className="text-blue-600">
                    {
                      spatialIndexingState.performanceMetrics
                        .optimizationsApplied.length
                    }
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Component Tooltip - Shows detailed component info on hover */}
      <MemoizedComponentTooltip
        component={components.find(c => c.id === hoveredComponent) || null}
        isVisible={!!hoveredComponent}
        scale={zoom}
      />
    </div>
  );
};
